/**
 * Web Vitals Performance Monitoring
 * 
 * Tracks Core Web Vitals metrics:
 * - LCP (Largest Contentful Paint) - loading performance
 * - FID (First Input Delay) - interactivity (legacy)
 * - INP (Interaction to Next Paint) - interactivity (new)
 * - CLS (Cumulative Layout Shift) - visual stability
 * - TTFB (Time to First Byte) - server response time
 * - FCP (First Contentful Paint) - initial render
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

export interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
  timestamp: number;
  path: string;
}

export interface WebVitalsThresholds {
  LCP: { good: number; poor: number };
  FID: { good: number; poor: number };
  INP: { good: number; poor: number };
  CLS: { good: number; poor: number };
  TTFB: { good: number; poor: number };
  FCP: { good: number; poor: number };
}

// Google's Core Web Vitals thresholds
export const WEB_VITALS_THRESHOLDS: WebVitalsThresholds = {
  LCP: { good: 2500, poor: 4000 },      // milliseconds
  FID: { good: 100, poor: 300 },         // milliseconds (legacy)
  INP: { good: 200, poor: 500 },         // milliseconds
  CLS: { good: 0.1, poor: 0.25 },        // score
  TTFB: { good: 800, poor: 1800 },       // milliseconds
  FCP: { good: 1800, poor: 3000 },       // milliseconds
};

// Store metrics for analysis
let collectedMetrics: WebVitalsMetric[] = [];

/**
 * Convert web-vitals metric to our format
 */
function formatMetric(metric: Metric): WebVitalsMetric {
  return {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType || 'unknown',
    timestamp: Date.now(),
    path: window.location.pathname,
  };
}

/**
 * Default reporter - logs to console in development
 */
function consoleReporter(metric: WebVitalsMetric): void {
  const emoji = metric.rating === 'good' ? '✅' : metric.rating === 'needs-improvement' ? '⚠️' : '❌';
  
  console.log(
    `${emoji} [Web Vitals] ${metric.name}: ${metric.value.toFixed(metric.name === 'CLS' ? 3 : 0)}${metric.name === 'CLS' ? '' : 'ms'} (${metric.rating})`
  );
}

/**
 * Analytics reporter - sends to analytics endpoint
 */
async function analyticsReporter(metric: WebVitalsMetric): Promise<void> {
  // Only send in production
  if (import.meta.env.DEV) return;
  
  try {
    // Use sendBeacon for reliable delivery
    const blob = new Blob([JSON.stringify({
      type: 'web-vitals',
      metric: metric,
      userAgent: navigator.userAgent,
      url: window.location.href,
    })], { type: 'application/json' });
    
    // Replace with your analytics endpoint
    const endpoint = '/api/analytics/web-vitals';
    navigator.sendBeacon(endpoint, blob);
  } catch (error) {
    // Silently fail - don't impact user experience
    console.debug('[Web Vitals] Failed to send metric:', error);
  }
}

// Generate a stable session ID for web vitals tracking
let webVitalsSessionId: string | null = null;
function getWebVitalsSessionId(): string {
  if (!webVitalsSessionId) {
    webVitalsSessionId = crypto.randomUUID();
  }
  return webVitalsSessionId;
}

/**
 * Supabase reporter - stores in database for analysis
 */
async function supabaseReporter(metric: WebVitalsMetric): Promise<void> {
  // Only send in production
  if (import.meta.env.DEV) return;
  
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Skip analytics for unauthenticated users to avoid RLS violations
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    // Use a valid UUID for session_id instead of metric.id which is not UUID format
    await supabase.from('analytics_events').insert({
      event_category: 'system',
      event_name: `web_vital_${metric.name.toLowerCase()}`,
      session_id: getWebVitalsSessionId(),
      page_path: metric.path,
      event_data: {
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
        navigationType: metric.navigationType,
        metricId: metric.id, // Store original metric.id for reference
      },
      device_info: {
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        connection: (navigator as any).connection?.effectiveType || 'unknown',
      },
    });
  } catch (error) {
    console.debug('[Web Vitals] Failed to store metric:', error);
  }
}

export type MetricReporter = (metric: WebVitalsMetric) => void | Promise<void>;

/**
 * Initialize Web Vitals monitoring
 */
export function initWebVitals(options?: {
  reporters?: MetricReporter[];
  enableConsoleLogging?: boolean;
  enableAnalytics?: boolean;
  enableSupabase?: boolean;
}): void {
  const {
    reporters = [],
    enableConsoleLogging = import.meta.env.DEV,
    enableAnalytics = !import.meta.env.DEV,
    enableSupabase = !import.meta.env.DEV,
  } = options || {};

  const allReporters: MetricReporter[] = [
    ...reporters,
    ...(enableConsoleLogging ? [consoleReporter] : []),
    ...(enableAnalytics ? [analyticsReporter] : []),
    ...(enableSupabase ? [supabaseReporter] : []),
  ];

  const handleMetric = (metric: Metric) => {
    const formattedMetric = formatMetric(metric);
    collectedMetrics.push(formattedMetric);
    
    allReporters.forEach(reporter => {
      try {
        reporter(formattedMetric);
      } catch (error) {
        console.debug('[Web Vitals] Reporter error:', error);
      }
    });
  };

  // Register all metric collectors
  onCLS(handleMetric);
  onFCP(handleMetric);
  onINP(handleMetric);
  onLCP(handleMetric);
  onTTFB(handleMetric);
}

/**
 * Get all collected metrics
 */
export function getCollectedMetrics(): WebVitalsMetric[] {
  return [...collectedMetrics];
}

/**
 * Clear collected metrics
 */
export function clearCollectedMetrics(): void {
  collectedMetrics = [];
}

/**
 * Get performance summary
 */
export function getPerformanceSummary(): {
  overall: 'good' | 'needs-improvement' | 'poor';
  metrics: Record<string, WebVitalsMetric | undefined>;
  score: number;
} {
  const latest: Record<string, WebVitalsMetric> = {};
  
  // Get latest of each metric type
  collectedMetrics.forEach(m => {
    if (!latest[m.name] || m.timestamp > latest[m.name].timestamp) {
      latest[m.name] = m;
    }
  });

  // Calculate overall score (0-100)
  const metricScores: number[] = [];
  Object.values(latest).forEach(m => {
    if (m.rating === 'good') metricScores.push(100);
    else if (m.rating === 'needs-improvement') metricScores.push(50);
    else metricScores.push(0);
  });

  const avgScore = metricScores.length > 0 
    ? metricScores.reduce((a, b) => a + b, 0) / metricScores.length 
    : 100;

  const overall = avgScore >= 90 ? 'good' : avgScore >= 50 ? 'needs-improvement' : 'poor';

  return {
    overall,
    metrics: latest,
    score: Math.round(avgScore),
  };
}
